export const SOURCE_DESCRIPTION =
  "A reference to a spatial thing. Use 'place' for free-text geocoding, 'feature' for a specific row, 'feature_set' for a SQL-defined result set, 'geometry' for inline ArcGIS JSON, 'extent' for a bounding box, 'view' for the current map view's extent, 'selection' for the user's click-selected feature, or 'handle' for a result returned by a prior tool.";

export const SourceSchema = {
  description: SOURCE_DESCRIPTION,
  oneOf: [
    {
      type: 'object',
      properties: {
        kind: { const: 'place' },
        text: { type: 'string', description: 'Free-text place name to geocode.' },
        max_results: { type: 'integer', minimum: 1, maximum: 10 },
      },
      required: ['kind', 'text'],
      additionalProperties: false,
    },
    {
      type: 'object',
      properties: {
        kind: { const: 'feature' },
        layer_id: { type: 'string' },
        objectid: { type: 'integer' },
      },
      required: ['kind', 'layer_id', 'objectid'],
      additionalProperties: false,
    },
    {
      type: 'object',
      properties: {
        kind: { const: 'feature_set' },
        layer_id: { type: 'string' },
        where: {
          type: 'string',
          description: 'SQL WHERE clause; use "1=1" for the entire layer.',
        },
        order_by: { type: 'string' },
        order_dir: { enum: ['ASC', 'DESC'] },
        limit: { type: 'integer', minimum: 1, maximum: 5000 },
        in_current_extent: { type: 'boolean' },
        spatial_filter: {
          type: 'object',
          description:
            'Optional nested Source. Restricts results to features intersecting this geometry — pass a handle to a buffer/place/extent/feature, or any other Source.',
        },
      },
      required: ['kind', 'layer_id', 'where'],
      additionalProperties: false,
    },
    {
      type: 'object',
      properties: {
        kind: { const: 'geometry' },
        geometry: {
          type: 'object',
          description:
            'ArcGIS JSON geometry. Point: {x,y,spatialReference}. Polyline: {paths,spatialReference}. Polygon: {rings,spatialReference}.',
        },
      },
      required: ['kind', 'geometry'],
      additionalProperties: false,
    },
    {
      type: 'object',
      properties: {
        kind: { const: 'extent' },
        xmin: { type: 'number' },
        ymin: { type: 'number' },
        xmax: { type: 'number' },
        ymax: { type: 'number' },
        wkid: { type: 'integer' },
      },
      required: ['kind', 'xmin', 'ymin', 'xmax', 'ymax'],
      additionalProperties: false,
    },
    {
      type: 'object',
      properties: { kind: { const: 'selection' } },
      required: ['kind'],
      additionalProperties: false,
    },
    {
      type: 'object',
      description:
        "Resolves to the current map view's extent — use as a spatial_filter or zoom target without round-tripping coordinates.",
      properties: { kind: { const: 'view' } },
      required: ['kind'],
      additionalProperties: false,
    },
    {
      type: 'object',
      properties: {
        kind: { const: 'handle' },
        id: { type: 'string' },
      },
      required: ['kind', 'id'],
      additionalProperties: false,
    },
  ],
} as const;

export type SourceJsonSchema = typeof SourceSchema;
